import React from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { animationTransition } from '@shared/utils/animationTransition';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { pxToRem } from '@withjoy/joykit/theme';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import { useTranslation } from '@shared/core';
import { StarRating } from '@shared/components';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { DistanceStatistics } from '@shared/components/DistanceStatistics';
import { ExpediaLogo } from '@assets/icons';
import { ReactComponent as JoyLogo } from '@assets/joy-logo.svg';
import { Close } from '@withjoy/joykit/icons';
import { AccommodationPropertyWithCalculatedPrice } from '@shared/hooks/useSearchExtendedAccommodationProperties';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

interface HotelCardTileProps
  extends AccommodationPropertyWithCalculatedPrice,
    Readonly<{
      onCloseCard: () => void;
    }> {}

// @todo - Going to use with hotel card in same map
export const HotelCardTile = (props: HotelCardTileProps) => {
  const { price, strikeOutPrice, percentageOff, distance, onCloseCard } = props;

  const { t } = useTranslation('websiteEditor');
  const hotelTileTrans = t('venueIntro', 'hotelTile');
  const isMobileOrTablet = useIsMobileOrTablet();

  const images = props.photos.filter(photo => photo.thumbnailUrl).map(photo => ({ src: photo.thumbnailUrl || '', alt: props.name || '' })) ?? [
    { src: DEFAULT_HOTEL_IMAGE, alt: props.name || '' },
  ];

  return (
    <>
      <Box
        aspectRatio="3/2"
        flexShrink={0}
        transition={animationTransition('opacity')}
        position={'relative'}
        backgroundColor={'mono2'}
        overflow={'hidden'}
        borderRadius={pxToRem(10.5)}
        maxHeight={isMobileOrTablet ? pxToRem(86) : pxToRem(158)}
      >
        <HotelPhotoCarousel
          hotel={props}
          images={images}
          context="Accommodations.HotelCardTile"
          aspectRatio={isMobileOrTablet ? '126/43' : '126/79'}
          navigation={{ variant: 'minimal', visible: 'default' }}
        />
        {/* @todo Tags would come up with V2 */}
        {/* <Flex
          position={'absolute'}
          top={2}
          right={3}
          gap={1}
          paddingX={4}
          height={pxToRem(28)}
          borderRadius={pxToRem(14)}
          border="0.875px solid rgba(255, 255, 255, 0.50)"
          background="rgba(255, 255, 255, 0.72)"
          alignItems={'center'}
        >
          <TextV2 typographyVariant={'label1'} display={'flex'} alignItems={'center'} fontWeight={500}>
            {hotelTileTrans.closestToVenue()}
          </TextV2>
        </Flex> */}
        <Flex
          width={7}
          height={7}
          borderRadius={5}
          alignItems={'center'}
          justifyContent={'center'}
          backdropFilter={'blur(3.4px)'}
          backgroundColor={'rgba(255, 255, 255, 0.50)'}
          position={'absolute'}
          top={3}
          left={3}
          cursor={'pointer'}
          onClick={onCloseCard}
        >
          <Close size={'sm'} />
        </Flex>
      </Box>
      {props?.name && (
        <Flex flexDirection={'column'}>
          <TextEllipsisWrapper
            typographyVariant={'label3'}
            color={'mono14'}
            lineClamp={2}
            fontSize={14.875}
          >
            {props.name}
          </TextEllipsisWrapper>
          {props.starRating && (
            <Flex marginTop={pxToRem(3.5)}>
              <StarRating
                id={`${props.joyPlaceId}`}
                rating={props.starRating}
                showPartialRating
                iconSize={16}
              />
            </Flex>
          )}
          {distance && (
            <Flex
              gap={pxToRem(10.5)}
              alignItems={'center'}
              marginTop={pxToRem(3.5)}
            >
              <DistanceFromVenue
                milesToVenue={distance}
                color="mono14"
                useToWording
                fontSize={pxToRem(12.25)}
              />
              <DistanceStatistics
                milesToVenue={distance}
                color="mono14"
                useShortWording
                fontSize={pxToRem(12.25)}
              />
            </Flex>
          )}
          {price && (
            <Flex
              marginTop={pxToRem(10.5)}
              justifyContent={'space-between'}
              paddingBottom={pxToRem(7)}
            >
              <Flex
                flexDirection={'column'}
                gap={pxToRem(7)}
                justifyContent={'space-between'}
                alignItems={'center'}
              >
                <TextV2
                  typographyVariant={'label2'}
                  fontSize={pxToRem(12.25)}
                  fontWeight={600}
                >
                  {hotelTileTrans.forOurGuests()}
                </TextV2>
                <Flex
                  gap={pxToRem(7)}
                  alignItems={'center'}
                >
                  <JoyLogo
                    color="white"
                    style={{ backgroundColor: '#494DCB', width: pxToRem(21), height: pxToRem(21), borderRadius: '50%', padding: pxToRem(2) }}
                  />
                  <TextV2
                    typographyVariant={'label2'}
                    fontSize={pxToRem(12.25)}
                    color={'#000000'}
                    fontWeight={500}
                  >
                    {hotelTileTrans.joyTravel()}
                  </TextV2>
                </Flex>
              </Flex>
              <Flex
                flexDirection={'column'}
                gap={pxToRem(3.5)}
                flexWrap={'wrap-reverse'}
                justifyContent={'flex-end'}
              >
                {percentageOff && (
                  <TextV2
                    typographyVariant="label2"
                    fontWeight={500}
                    paddingX={pxToRem(7)}
                    paddingY={pxToRem(3.5)}
                    fontSize={pxToRem(11.375)}
                    lineHeight={'18px'}
                    borderRadius={'full'}
                    backgroundColor={'accentGreen2'}
                    color={'accentGreen3'}
                    width={'fit-content'}
                    whiteSpace={'nowrap'}
                  >
                    {hotelTileTrans.savePercentageOff({ percentageOff })}
                  </TextV2>
                )}
                <Flex gap={pxToRem(3.5)}>
                  {strikeOutPrice && (
                    <TextV2
                      typographyVariant={'label2'}
                      fontSize={pxToRem(14)}
                      fontWeight={500}
                      color={'mono8'}
                      textDecorationLine={'line-through'}
                    >
                      {strikeOutPrice}
                    </TextV2>
                  )}
                  <TextV2
                    typographyVariant={'label2'}
                    fontSize={pxToRem(14)}
                    fontWeight={600}
                  >
                    {hotelTileTrans.pricePerNight({ price })}
                  </TextV2>
                </Flex>
              </Flex>
            </Flex>
          )}
          {strikeOutPrice && (
            <Flex
              paddingTop={pxToRem(10.5)}
              justifyContent={'space-between'}
              borderTop={'0.875px solid'}
              borderColor={'mono3'}
              alignItems={'center'}
            >
              <ExpediaLogo />
              <TextV2
                typographyVariant={'label2'}
                fontSize={pxToRem(12.25)}
                fontWeight={500}
                color={'mono8'}
              >
                {hotelTileTrans.strikeOutPrice({ strikeOutPrice })}
              </TextV2>
            </Flex>
          )}
        </Flex>
      )}
    </>
  );
};
